import Axios from 'axios'
import AxiosIsCancel from 'axios/lib/cancel/isCancel'
import isPlainObject from 'lodash/isPlainObject' // optionally, use lodash for reliability
import { useAppStore } from '../store'
import { msalInstance as msal } from './msalConfig'

const baseAPIURL = config.VUE_APP_BASE_URL || process.env.VUE_APP_BASE_URL
const axios = Axios.create({
  baseURL: baseAPIURL
})

/**
 * Axios request interceptor to modify requests before they are sent.
 *
 * Setzt den Authorization-Header mit einem per MSAL beschafften Access-Token und
 * haengt die merchantId an. Frueher stand hier ein statischer, im Bundle
 * ausgelieferter Bearer-Token, und die Identitaet wurde als `userid`-Parameter
 * mitgeschickt — beides zusammen erlaubte es jedem Besucher, sich als beliebiger
 * Nutzer auszugeben. Die Identitaet kommt jetzt ausschliesslich aus dem Token,
 * `userid` wird nicht mehr gesendet.
 *
 * Die merchantId bleibt ein Request-Parameter (der Nutzer waehlt seine Firma),
 * wird backendseitig aber gegen die HubSpot-Zuordnung des Nutzers geprueft.
 */
axios.interceptors.request.use(
  async (requestConfig) => {
    const merchantId = sessionStorage.getItem('companyId')

    const token = await msal.acquireApiToken()
    if (token) {
      requestConfig.headers.Authorization = 'Bearer ' + token
    }

    if (requestConfig.method === 'get') {
      const url = new URL(requestConfig.url, window.location.origin)
      if (merchantId) url.searchParams.append('merchantId', merchantId)
      requestConfig.url = url.pathname + '?' + url.searchParams.toString()
    } else if (['post', 'put', 'delete'].includes(requestConfig.method)) {
      if (isPlainObject(requestConfig.data)) {
        requestConfig.data = { ...requestConfig.data, merchantId }
      } else if (requestConfig.data instanceof FormData) {
        if (merchantId) requestConfig.data.append('merchantId', merchantId)
      } else {
        requestConfig.data = { merchantId }
      }
    } else {
      console.error('Request method not supported for adding merchantId')
    }
    return requestConfig
  },
  async (error) => {
    return Promise.reject(error).finally(console.clear)
  }
)

axios.interceptors.response.use(
  async (res) => {
    if (res.data.error) {
      const requestUrl = res.config ? (res.config.baseURL || '') + res.config.url : 'Unknown'
      // FIXME: ignore errors for delivery-stock-details and download-latest
      if (!AxiosIsCancel(res) && !requestUrl.includes('delivery-stock-details') && !requestUrl.includes('download-latest')) {
        const errorMessage = res.data.error.message || res.data.error
        const errorDetails = res.data.error.details || res.data.error.stack || null
        const requestMethod = res.config ? res.config.method.toUpperCase() : 'Unknown'
        const requestPayload = res.config && res.config.method.toLowerCase() === 'post' ? res.config.data : null

        console.error(errorMessage)
        const appStore = useAppStore()
        appStore.SET_ERROR({
          message: errorMessage,
          details: errorDetails,
          url: requestUrl,
          method: requestMethod,
          payload: requestPayload
        })
        // Reset global loading route state if active
        if (appStore.getLoadingRoute) {
          appStore.SET_LOADING_ROUTE(false)
        }
      }
    }
    return res
  },
  async (error) => {
    const requestUrl = error.config ? (error.config.baseURL || '') + error.config.url : 'Unknown'
    // FIXME: ignore errors for delivery-stock-details and download-latest
    if (!AxiosIsCancel(error) && !requestUrl.includes('delivery-stock-details') && !requestUrl.includes('download-latest')) {
      const errorMessage = error.message || 'An error occurred'
      const errorDetails = error.stack || (error.response ? JSON.stringify(error.response.data, null, 2) : null)
      const requestMethod = error.config ? error.config.method.toUpperCase() : 'Unknown'
      const requestPayload = error.config && error.config.method.toLowerCase() === 'post' ? error.config.data : null

      const appStore = useAppStore()
      appStore.SET_ERROR({
        message: errorMessage,
        details: errorDetails,
        url: requestUrl,
        method: requestMethod,
        payload: requestPayload
      })
      // Reset global loading route state if active
      if (appStore.getLoadingRoute) {
        appStore.SET_LOADING_ROUTE(false)
      }
    }
    return Promise.reject(error)
  }
)

export default axios
